import { IconChevronLeft } from '../components/talkAppleIcons';
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { isGuestUser } from '../constants/auth';
import { TALK_PREMIUM_ROOMS } from '../data/talkRoomsConfig';
import IapSubscriptionDetails from '../components/IapSubscriptionDetails';
import RestorePurchasesPanel from '../components/RestorePurchasesPanel';
import {
  isIosNativeCheckout,
  getPremiumPurchaseButtonLabel,
  loadIapPremiumProduct,
  purchasePremium,
  openIosManageSubscriptions,
} from '../utils/checkout';

export default function TalkPremium() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, refreshMe, upgradeGuest, login } = useAuth();
  const [purchaseBusy, setPurchaseBusy] = useState(false);
  const [purchaseErr, setPurchaseErr] = useState('');
  const [statusMsg, setStatusMsg] = useState('');
  const [billingInfo, setBillingInfo] = useState(null);
  const [checkoutUrl, setCheckoutUrl] = useState('');
  const [iapProduct, setIapProduct] = useState(null);
  const [iapLoadErr, setIapLoadErr] = useState('');
  const [iapRetryKey, setIapRetryKey] = useState(0);
  const [selectedPlan, setSelectedPlan] = useState('yearly');

  const useAppleIap = isIosNativeCheckout();
  const purchaseButtonLabel = getPremiumPurchaseButtonLabel();

  const [authMode, setAuthMode] = useState('register');
  const [authUsername, setAuthUsername] = useState('');
  const [authEmail, setAuthEmail] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [authLoading, setAuthLoading] = useState(false);

  const premiumActive = user && Number(user.talk_premium) === 1;
  const isGuest = isGuestUser(user);
  const stripeReady = !useAppleIap && billingInfo?.checkoutAvailable;
  const demoAllowed = billingInfo?.demoPurchaseAllowed;
  const iapProductLoading = useAppleIap && !iapProduct && !iapLoadErr;
  // 商品情報が取れない状態で購入ボタンを押させない（押すと StoreKit のエラーが出るだけ）
  const iapUnavailable = useAppleIap && !iapProduct && Boolean(iapLoadErr);

  const loadBillingStatus = () => {
    axios.get('/api/billing/premium/status')
      .then((r) => setBillingInfo(r.data))
      .catch(() => setBillingInfo({ stripeConfigured: false, checkoutAvailable: false, demoPurchaseAllowed: true }));
  };

  useEffect(() => {
    loadBillingStatus();
  }, []);

  useEffect(() => {
    if (user && !isGuestUser(user)) {
      loadBillingStatus();
    }
  }, [user?.id, user?.email]);

  useEffect(() => {
    if (!useAppleIap) return undefined;
    let cancelled = false;
    setIapLoadErr('');
    loadIapPremiumProduct()
      .then((p) => {
        if (cancelled) return;
        if (p) setIapProduct(p);
        else setIapLoadErr('ただいま App Store に接続できません');
      })
      .catch(() => {
        if (!cancelled) {
          setIapLoadErr('ただいま App Store に接続できません');
        }
      });
    return () => { cancelled = true; };
  }, [useAppleIap, iapRetryKey]);

  useEffect(() => {
    const checkout = searchParams.get('checkout');
    const sessionId = searchParams.get('session_id');
    if (checkout === 'cancel') {
      setStatusMsg('お支払いはキャンセルされました。');
      setSearchParams({}, { replace: true });
      return;
    }
    if (checkout !== 'success' || !sessionId) return;

    let cancelled = false;
    (async () => {
      setPurchaseBusy(true);
      setStatusMsg('お支払いを確認しています…');
      try {
        const { data } = await axios.get('/api/billing/premium/verify', {
          params: { session_id: sessionId },
        });
        if (cancelled) return;
        if (data.pending) {
          setStatusMsg('決済処理中です。しばらくしてからページを更新してください。');
        } else if (data.ok) {
          await refreshMe();
          setStatusMsg('プレミアムが有効になりました。詳細をメールでお送りしました。');
        }
      } catch (e) {
        if (!cancelled) {
          setPurchaseErr(e?.response?.data?.error || '決済の確認に失敗しました');
        }
      } finally {
        if (!cancelled) setPurchaseBusy(false);
        setSearchParams({}, { replace: true });
      }
    })();

    return () => { cancelled = true; };
  }, [searchParams, setSearchParams, refreshMe]);

  const handleGuestUpgrade = async (e) => {
    e.preventDefault();
    setAuthError('');
    setAuthLoading(true);
    try {
      await upgradeGuest({
        username: authUsername.trim(),
        email: authEmail.trim(),
        password: authPassword,
        accepted_terms: true,
      });
      setAuthPassword('');
      if (useAppleIap || billingInfo?.checkoutAvailable) {
        await runPremiumPurchase({ skipGuestCheck: true });
      } else {
        setStatusMsg('登録が完了しました。決済機能の設定後にお支払いください。');
      }
    } catch (err) {
      setAuthError(err?.response?.data?.error || '登録に失敗しました');
    } finally {
      setAuthLoading(false);
    }
  };

  const handleGuestLogin = async (e) => {
    e.preventDefault();
    setAuthError('');
    setAuthLoading(true);
    try {
      await login(authEmail.trim(), authPassword);
      setAuthPassword('');
      setStatusMsg('ログインしました。お支払い画面へ進みます…');
      await runPremiumPurchase({ skipGuestCheck: true });
    } catch (err) {
      setAuthError(err?.response?.data?.error || 'ログインに失敗しました');
    } finally {
      setAuthLoading(false);
    }
  };

  const runPremiumPurchase = async ({ skipGuestCheck = false, plan = selectedPlan } = {}) => {
    if (!skipGuestCheck && isGuest) return;
    setPurchaseErr('');
    setStatusMsg('');
    setCheckoutUrl('');
    setPurchaseBusy(true);
    try {
      const result = await purchasePremium({ refreshMe: useAppleIap ? refreshMe : undefined, plan });
      if (result?.method === 'ios_iap' || result?.method === 'ios_iap_restore') {
        setStatusMsg('プレミアムが有効になりました。');
      } else if (result?.method === 'stripe') {
        setCheckoutUrl(result.url);
        setStatusMsg('Stripe のお支払い画面へ移動します…');
      }
    } catch (e) {
      if (e?.cancelled) return;
      const msg = e?.response?.data?.error || e?.message || '購入に失敗しました';
      setPurchaseErr(msg);
      if (e?.checkoutUrl) setCheckoutUrl(e.checkoutUrl);
    } finally {
      setPurchaseBusy(false);
    }
  };

  const openAppleSubscriptions = async () => {
    setPurchaseErr('');
    setStatusMsg('');
    try {
      await openIosManageSubscriptions();
    } catch (e) {
      setPurchaseErr(e?.message || 'サブスクリプション管理画面を開けませんでした');
    }
  };

  const purchaseWithCard = async () => {
    if (isGuest || iapUnavailable) return;
    await runPremiumPurchase({ plan: selectedPlan });
  };

  const retryIapProduct = () => {
    setPurchaseErr('');
    setIapLoadErr('');
    setIapRetryKey((k) => k + 1);
  };

  const openCustomerPortal = async () => {
    setPurchaseErr('');
    setStatusMsg('');
    setPurchaseBusy(true);
    try {
      const { data } = await axios.post('/api/billing/premium/portal');
      if (data?.url) {
        window.location.assign(data.url);
      }
    } catch (e) {
      setPurchaseErr(e?.response?.data?.error || '管理ページを開けませんでした');
    } finally {
      setPurchaseBusy(false);
    }
  };

  const renderPurchaseActions = () => {
    if (premiumActive) {
      return (
        <>
          <p style={S.statusOk}>プレミアムが有効です。トーク一覧からルームに入室できます。</p>
          <button data-control="secondary" data-control-id="TalkPremium-secondaryBtn" type="button" style={S.secondaryBtn} onClick={() => navigate('/talk')}>
            トークルームへ
          </button>
          {stripeReady && (
            <button data-control="secondary" data-control-id="TalkPremium-secondaryBtn"
              type="button"
              style={{ ...S.secondaryBtn, marginTop: 8, ...(purchaseBusy ? S.primaryBtnDisabled : {}) }}
              onClick={openCustomerPortal}
              disabled={purchaseBusy}
            >
              {purchaseBusy ? '処理中…' : 'サブスクを管理する（解約・カード変更）'}
            </button>
          )}
          {useAppleIap && (
            <button data-control="secondary" data-control-id="TalkPremium-secondaryBtn"
              type="button"
              style={{ ...S.secondaryBtn, marginTop: 8 }}
              onClick={openAppleSubscriptions}
            >
              サブスクリプションを管理する（解約）
            </button>
          )}
          {/* 別端末で購読中の場合にもここから復元できるようにする（ガイドライン 3.1.1） */}
          <RestorePurchasesPanel variant="inline" onRestored={refreshMe} />
          {purchaseErr ? <p style={S.err}>{purchaseErr}</p> : null}
        </>
      );
    }
    if (isGuest) {
      return (
        <>
          {useAppleIap && (
            <IapSubscriptionDetails
              product={iapProduct}
              loading={iapProductLoading}
              error={iapLoadErr}
              onRetry={retryIapProduct}
            />
          )}
          <p style={S.emailNote}>
            {useAppleIap
              ? 'App Store での購入にはメール登録が必要です（ゲストのままでは購入できません）。'
              : 'クレジットカード決済にはメール登録が必要です（ゲストのままでは購入できません）。'}
          </p>
          <div style={S.authTabs}>
            <button data-control="tab" data-control-id="TalkPremium-authTab"
              type="button"
              aria-pressed={authMode === 'register'} style={{ ...S.authTab, ...(authMode === 'register' ? S.authTabActive : {}) }}
              onClick={() => { setAuthMode('register'); setAuthError(''); }}
            >
              新規登録
            </button>
            <button data-control="tab" data-control-id="TalkPremium-authTab"
              type="button"
              aria-pressed={authMode === 'login'} style={{ ...S.authTab, ...(authMode === 'login' ? S.authTabActive : {}) }}
              onClick={() => { setAuthMode('login'); setAuthError(''); }}
            >
              ログイン
            </button>
          </div>
          <form
            onSubmit={authMode === 'register' ? handleGuestUpgrade : handleGuestLogin}
            style={S.authForm}
          >
            {authMode === 'register' && (
              <label style={S.authLabel}>
                ユーザー名
                <input
                  style={S.authInput}
                  value={authUsername}
                  onChange={(e) => setAuthUsername(e.target.value)}
                  required
                  autoComplete="username"
                />
              </label>
            )}
            <label style={S.authLabel}>
              メール
              <input
                style={S.authInput}
                type="email"
                value={authEmail}
                onChange={(e) => setAuthEmail(e.target.value)}
                required
                autoComplete="email"
              />
            </label>
            <label style={S.authLabel}>
              パスワード
              <input
                style={S.authInput}
                type="password"
                value={authPassword}
                onChange={(e) => setAuthPassword(e.target.value)}
                required
                minLength={6}
                autoComplete={authMode === 'login' ? 'current-password' : 'new-password'}
              />
            </label>
            {authError ? <p style={S.err}>{authError}</p> : null}
            <button data-control="primary" data-control-id="TalkPremium-primaryBtn"
              type="submit"
              style={{ ...S.primaryBtn, ...(authLoading ? S.primaryBtnDisabled : {}) }}
              disabled={authLoading}
            >
              {authLoading ? '処理中…' : authMode === 'register' ? '登録して決済へ進む' : 'ログイン'}
            </button>
          </form>
          {/* 購入済みユーザーは登録前でも復元できる必要がある（ガイドライン 3.1.1） */}
          <RestorePurchasesPanel variant="inline" onRestored={refreshMe} />
        </>
      );
    }
    if (useAppleIap) {
      return (
        <>
          <IapSubscriptionDetails
            product={iapProduct}
            loading={iapProductLoading}
            error={iapLoadErr}
            onRetry={retryIapProduct}
          />
          <button data-control="primary" data-control-id="TalkPremium-cardBtn"
            type="button"
            style={{
              ...S.cardBtn,
              ...(purchaseBusy || iapProductLoading || iapUnavailable ? S.primaryBtnDisabled : {}),
            }}
            onClick={purchaseWithCard}
            disabled={purchaseBusy || iapProductLoading || iapUnavailable}
          >
            
            {purchaseBusy ? '処理中…' : purchaseButtonLabel}
          </button>
          <RestorePurchasesPanel variant="inline" onRestored={refreshMe} />
          {purchaseErr ? <p style={S.err}>{purchaseErr}</p> : null}
        </>
      );
    }
    return (
      <>
        {stripeReady ? (
          <p style={S.emailNote}>
            {`登録メール（${user?.email}）に、プラン内容と決済リンクの詳細をお送りします。`}
          </p>
        ) : (
          <p style={S.apiOff}>現在、購入手続きをご利用いただけません。しばらくしてからもう一度お試しください。</p>
        )}
        <button data-control="primary" data-control-id="TalkPremium-cardBtn"
          type="button"
          style={{ ...S.cardBtn, ...(purchaseBusy || !stripeReady ? S.primaryBtnDisabled : {}) }}
          onClick={purchaseWithCard}
          disabled={purchaseBusy || !stripeReady}
        >
          
          {purchaseBusy ? '処理中…' : purchaseButtonLabel}
        </button>
        {checkoutUrl ? (
          <a data-control="text" data-control-id="TalkPremium-checkoutLink"
            href={checkoutUrl}
            style={S.checkoutLink}
            rel="noopener noreferrer"
            onClick={() => setStatusMsg('お支払い画面へ移動します…')}
          >
            決済ページを開く
          </a>
        ) : null}
        {purchaseErr ? <p style={S.err}>{purchaseErr}</p> : null}
        <p style={S.legal}>
          Visa / Mastercard / Amex など。Stripe の安全な決済ページでカード情報を入力します。
        </p>
      </>
    );
  };

  return (
    <div className="jt-premium-page" style={S.page}>
      <header style={S.header}>
        <button data-control="icon" data-control-id="TalkPremium-back" type="button" style={S.back} onClick={() => navigate(-1)} aria-label="戻る"><IconChevronLeft /></button>
        <h1 style={S.title}>プレミアムプラン</h1>
        <span style={S.headerSpacer} />
      </header>

      <section className="jt-plan-intro" aria-labelledby="plan-intro-title">
        <h2 id="plan-intro-title">時間を気にせず話す</h2>
        <p>無料プランではライブルームを1時間まで利用できます。プレミアムでは時間制限なく利用できます。</p>
        <dl className="jt-plan-status"><dt>現在のプラン</dt><dd>{premiumActive ? 'プレミアム' : '無料プラン'}</dd></dl>
      </section>

      {statusMsg ? <p style={S.statusMsg}>{statusMsg}</p> : null}

      <section className="jt-plan-section" style={S.section}>
        <h2 className="jt-plan-section-title" style={S.sectionTitle}>含まれるルーム</h2>
        <ul className="jt-plan-rooms" style={S.roomList}>
          {TALK_PREMIUM_ROOMS.map((r) => (
            <li key={r.id} style={S.roomItem}>
              <span style={S.roomName}>{r.title}</span>
              <span style={S.roomDesc}>{r.desc}</span>
            </li>
          ))}
        </ul>
      </section>

      <section className="jt-plan-section" style={S.section}>
        {!useAppleIap && <h2 className="jt-plan-section-title" style={S.sectionTitle}>プランを選ぶ</h2>}

        {!useAppleIap && (
        <>
        {/* 月額 */}
        <button data-control="choice" data-control-id="TalkPremium-planCard"
          type="button"
          style={{ ...S.planCard, ...(selectedPlan === 'monthly' ? S.planCardSelected : {}) }}
          aria-pressed={selectedPlan === 'monthly'} onClick={() => setSelectedPlan('monthly')}
        >
          <div style={S.planHead}>
            <div>
              <span data-control-copy="label" style={S.planName}>月額プラン</span>
            </div>
            <div style={S.planPriceWrap}>
              <span data-control-copy="price" style={S.planPrice}>$15</span>
              <span data-control-copy="caption" style={S.planPer}>/月</span>
            </div>
          </div>
          <p data-control-copy="caption" style={S.planNote}>いつでも解約できます</p>
          {selectedPlan === 'monthly' && <span style={S.planCheck}>✓</span>}
        </button>

        {/* 年額（推奨） */}
        <button data-control="choice" data-control-id="TalkPremium-planCard"
          type="button"
          style={{ ...S.planCard, ...(selectedPlan === 'yearly' ? S.planCardSelected : {}), position: 'relative' }}
          aria-pressed={selectedPlan === 'yearly'} onClick={() => setSelectedPlan('yearly')}
        >
          <span data-control-copy="badge" style={S.planBadge}>45%オフ</span>
          <div style={S.planHead}>
            <div>
              <span data-control-copy="label" style={S.planName}>年額プラン</span>
            </div>
            <div style={S.planPriceWrap}>
              <span data-control-copy="price" style={S.planPrice}>$99</span>
              <span data-control-copy="caption" style={S.planPer}>/年</span>
            </div>
          </div>
          <p data-control-copy="caption" style={S.planNote}>月換算 $8.25（年額 $99）</p>
          {selectedPlan === 'yearly' && <span style={S.planCheck}>✓</span>}
        </button>

        {/* 永久 */}
        <button data-control="choice" data-control-id="TalkPremium-planCard"
          type="button"
          style={{ ...S.planCard, ...(selectedPlan === 'lifetime' ? S.planCardSelected : {}), position: 'relative' }}
          aria-pressed={selectedPlan === 'lifetime'} onClick={() => setSelectedPlan('lifetime')}
        >
          <span data-control-copy="badge" style={{ ...S.planBadge, background: "var(--surface)", color: "var(--primary-dark)" }}>買い切り</span>
          <div style={S.planHead}>
            <div>
              <span data-control-copy="label" style={S.planName}>永久プラン</span>
            </div>
            <div style={S.planPriceWrap}>
              <span data-control-copy="price" style={S.planPrice}>$199</span>
              <span data-control-copy="caption" style={S.planPer}>一括</span>
            </div>
          </div>
          <p data-control-copy="caption" style={S.planNote}>一度のお支払いで、プレミアムを期限なく利用できます。月額・年額の継続課金はありません。</p>
          {selectedPlan === 'lifetime' && <span style={S.planCheck}>✓</span>}
        </button>
        </>
        )}

        <ul style={S.planFeatures}>
          <li>ライブルームの利用時間が無制限</li>
          <li>プロフィールにプレミアムバッジ</li>
          <li>AI翻訳・添削無制限</li>
          {!useAppleIap && <li>お支払いの詳細を登録メールアドレスに送信</li>}
        </ul>
        {renderPurchaseActions()}
      </section>
    </div>
  );
}

const S = {
  page: {
    minHeight: '100vh',
    background: 'var(--bg)',
    paddingBottom: 'calc(120px + env(safe-area-inset-bottom))',
    fontFamily: 'inherit',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    padding: '10px 4px',
    background: 'var(--surface)',
    borderBottom: '1px solid var(--border)',
    position: 'sticky',
    top: 0,
    zIndex: 10,
    backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
  },
  back: {
    background: 'none',
    border: 'none',
    fontSize: 28,
    lineHeight: 1,
    cursor: 'pointer',
    color: 'var(--text)',
    fontWeight: 300,
    width: 44,
    height: 44,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  title: {
    flex: 1,
    fontSize: 16,
    fontWeight: 800,
    color: 'var(--text)',
    paddingLeft: 2,
  },
  headerSpacer: { width: 44 },
  hero: {
    margin: '16px 16px 0',
    padding: '20px 18px',
    borderRadius: 16,
    background: 'var(--primary-ultra)',
    border: '1px solid var(--primary-light)',
  },
  heroBadge: {
    fontSize: 22,
    color: 'var(--primary)',
  },
  heroLead: {
    margin: '8px 0 6px',
    fontSize: 20,
    fontWeight: 800,
    letterSpacing: '-0.03em',
    color: 'var(--text)',
  },
  heroDesc: {
    margin: 0,
    fontSize: 13,
    lineHeight: 1.6,
    color: 'var(--text-secondary)',
  },
  heroStatus: {
    margin: '14px 0 0',
    paddingTop: 12,
    borderTop: '1px solid var(--border)',
    fontSize: 13,
    color: 'var(--text)',
  },
  statusOn: { color: 'var(--primary-dark)' },
  statusOff: { color: 'var(--text-secondary)' },
  statusMsg: {
    margin: '12px 16px 0',
    padding: '12px 14px',
    borderRadius: 12,
    background: 'var(--primary-ultra)',
    border: '1px solid var(--primary-light)',
    color: 'var(--primary-dark)',
    fontSize: 13,
    lineHeight: 1.5,
  },
  section: { padding: '20px 16px 0' },
  sectionTitle: {
    margin: '0 0 12px',
    fontSize: 11,
    fontWeight: 800,
    color: 'var(--text-muted)',
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
  },
  roomList: {
    margin: 0,
    padding: 0,
    listStyle: 'none',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  roomItem: {
    padding: '12px 14px',
    borderRadius: 12,
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  roomName: { fontSize: 14, fontWeight: 700, color: 'var(--text)' },
  roomDesc: { fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.4 },
  planCard: {
    width: '100%', textAlign: 'left', fontFamily: 'inherit', cursor: 'pointer',
    padding: '14px 44px 14px 16px', borderRadius: 16, marginBottom: 10,
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    boxShadow: 'var(--shadow-sm)',
    position: 'relative', overflow: 'hidden',
    transition: 'border-color 0.15s',
  },
  planCardSelected: {
    border: '1.5px solid var(--primary)',
    background: 'var(--primary-ultra)',
    boxShadow: '0 6px 18px rgba(16,185,129,0.20)',
  },
  planBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    maxWidth: '100%',
    marginBottom: 9,
    fontSize: 10, fontWeight: 800, color: 'var(--surface)',
    background: 'var(--primary)',
    padding: '4px 10px',
    borderRadius: 9999,
    letterSpacing: '0.04em',
    lineHeight: 1.2,
    whiteSpace: 'normal',
  },
  planCheck: {
    position: 'absolute', right: 14, bottom: 14,
    fontSize: 16, color: 'var(--primary)',
  },
  planHead: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12,
    flexWrap: 'wrap',
  },
  planPriceWrap: { display: 'flex', alignItems: 'baseline', gap: 3, flexShrink: 0 },
  planName: { fontSize: 15, fontWeight: 700, color: 'var(--primary-dark)' },
  planNote: { margin: '4px 0 0', fontSize: 11, color: 'var(--text-secondary)', fontWeight: 400 },
  planPrice: { fontSize: 22, fontWeight: 800, color: 'var(--text)', letterSpacing: '-0.03em' },
  planPer: { fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)' },
  planFeatures: {
    margin: '12px 0 18px',
    paddingLeft: 18,
    fontSize: 13,
    color: 'var(--text-secondary)',
    lineHeight: 1.7,
  },
  emailNote: {
    margin: '0 0 14px',
    fontSize: 12,
    lineHeight: 1.55,
    color: 'var(--text-secondary)',
  },
  authTabs: {
    display: 'flex',
    gap: 8,
    marginBottom: 12,
  },
  authTab: {
    flex: 1,
    padding: '10px 8px',
    borderRadius: 10,
    border: '1px solid var(--border)',
    background: 'transparent',
    color: 'var(--text-secondary)',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  authTabActive: {
    border: '1px solid var(--primary)',
    color: 'var(--primary-dark)',
    background: 'var(--primary-ultra)',
  },
  authForm: { display: 'flex', flexDirection: 'column', gap: 10 },
  authLabel: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    fontSize: 11,
    fontWeight: 600,
    color: 'var(--text-secondary)',
  },
  authInput: {
    padding: '11px 12px',
    borderRadius: 10,
    border: '1px solid var(--border)',
    background: 'var(--bg)',
    color: 'var(--text)',
    fontSize: 14,
    fontFamily: 'inherit',
  },
  primaryBtn: {
    display: 'block',
    width: '100%',
    padding: '15px 16px',
    borderRadius: 'var(--r-btn)',
    border: 'none',
    cursor: 'pointer',
    fontSize: 15,
    fontWeight: 800,
    fontFamily: 'inherit',
    color: 'var(--surface)',
    background: 'var(--app-gradient)',
    boxShadow: '0 6px 18px rgba(16,185,129,0.35)',
  },
  cardBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    minHeight: 48,
    padding: '12px 16px',
    borderRadius: 4,
    border: '1px solid var(--primary-dark)',
    cursor: 'pointer',
    fontSize: 15,
    fontWeight: 600,
    fontFamily: 'inherit',
    color: '#fff',
    background: 'var(--primary-dark)',
  },
  primaryBtnDisabled: { opacity: 0.55, cursor: 'not-allowed' },
  secondaryBtn: {
    display: 'block',
    width: '100%',
    marginTop: 12,
    padding: '14px 16px',
    borderRadius: 14,
    border: '1px solid var(--primary)',
    cursor: 'pointer',
    fontSize: 15,
    fontWeight: 700,
    fontFamily: 'inherit',
    color: 'var(--primary-dark)',
    background: 'transparent',
  },
  statusOk: {
    margin: '0 0 4px',
    fontSize: 14,
    fontWeight: 700,
    color: 'var(--primary-dark)',
    lineHeight: 1.5,
  },
  err: { fontSize: 13, color: 'var(--error)', marginTop: 10, marginBottom: 0 },
  checkoutLink: {
    display: 'block',
    marginTop: 12,
    padding: '14px 16px',
    borderRadius: 14,
    border: '1px solid var(--primary-light)',
    background: 'var(--primary-ultra)',
    color: 'var(--primary-dark)',
    fontSize: 15,
    fontWeight: 700,
    textAlign: 'center',
    textDecoration: 'none',
  },
  legal: {
    margin: '12px 0 0',
    fontSize: 12,
    color: 'var(--text-secondary)',
    textAlign: 'left',
    lineHeight: 1.7,
  },
  apiOff: {
    margin: 0,
    fontSize: 13,
    color: 'var(--text-secondary)',
    lineHeight: 1.5,
  },
  code: {
    fontSize: 12,
    color: 'var(--primary-dark)',
    fontFamily: 'ui-monospace, monospace',
  },
  stripeDashLink: {
    color: 'var(--primary-dark)',
    fontWeight: 700,
    textDecoration: 'underline',
  },
};
